/* eslint-disable id-length */
/**
 * Module dependencies.
 */

import { FadeInUp } from 'components/atoms/in-view-once/fade-in-up';
import {
  FormContainer,
  FormStack,
  InputGroup,
  SubmitButton,
} from './contacts-form.style';

import { InViewOnce } from 'components/atoms/in-view-once/in-view-once';
import { JSX, useCallback } from 'react';
import { TextField } from 'components/atoms/forms/text-field/text-field';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import toast from 'react-hot-toast';

/**
 * Slot props.
 */

const slotProps = {
  input: {
    style: {
      fontSize: 14,
    },
  },
  inputLabel: {
    style: {
      fontSize: 14,
    },
  },
};

/**
 * Export `ContactsForm` component.
 */

export function ContactsForm(): JSX.Element {
  const { i18n, t: translate } = useTranslation();
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm();

  const submitData = useCallback(
    async (data) => {
      await new Promise((resolve) => {
        const timer = setTimeout(() => {
          clearTimeout(timer);
          resolve(null);
        }, 3000);
      });

      const response = await fetch('/api/contact-request', {
        body: JSON.stringify({
          ...data,
          locale: i18n.language,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to send message');
      }

      reset();
    },
    [i18n.language, reset],
  );

  const onSubmit = useCallback(
    (data) => {
      toast.promise(submitData(data), {
        error: translate('contacts.form.toast.error'),
        loading: translate('contacts.form.toast.loading'),
        success: translate('contacts.form.toast.success'),
      });
    },
    [submitData, translate],
  );

  return (
    <FormContainer component={'form'} onSubmit={handleSubmit(onSubmit)}>
      <FormStack>
        <InputGroup>
          <InViewOnce AnimationComponent={FadeInUp} style={{ flex: 1 }}>
            <TextField
              fullWidth
              label={translate('contacts.form.name')}
              slotProps={slotProps}
              {...register('name', {
                required: translate('contacts.form.errors.name.required'),
              })}
              error={!!errors.name}
              helperText={
                errors.name?.message ? String(errors.name.message) : undefined
              }
            />
          </InViewOnce>

          <InViewOnce AnimationComponent={FadeInUp} style={{ flex: 1 }}>
            <TextField
              fullWidth
              label={translate('contacts.form.email')}
              slotProps={slotProps}
              type={'email'}
              {...register('email', {
                pattern: {
                  message: translate('contacts.form.errors.email.pattern'),
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                },
                required: translate('contacts.form.errors.email.required'),
              })}
              error={!!errors.email}
              helperText={
                errors.email?.message ? String(errors.email.message) : undefined
              }
            />
          </InViewOnce>
        </InputGroup>

        <InViewOnce AnimationComponent={FadeInUp}>
          <TextField
            fullWidth
            label={translate('contacts.form.subject')}
            {...register('subject', {
              required: translate('contacts.form.errors.subject.required'),
            })}
            error={!!errors.subject}
            helperText={
              errors.subject?.message
                ? String(errors.subject.message)
                : undefined
            }
            slotProps={slotProps}
          />
        </InViewOnce>

        <InViewOnce AnimationComponent={FadeInUp}>
          <TextField
            fullWidth
            label={translate('contacts.form.message')}
            multiline
            rows={4}
            slotProps={slotProps}
            {...register('message', {
              required: translate('contacts.form.errors.message.required'),
            })}
            error={!!errors.message}
            helperText={
              errors.message?.message
                ? String(errors.message.message)
                : undefined
            }
          />
        </InViewOnce>

        <InViewOnce AnimationComponent={FadeInUp}>
          <SubmitButton size={'large'} type={'submit'} variant={'contained'}>
            {translate('contacts.form.submit')}
          </SubmitButton>
        </InViewOnce>
      </FormStack>
    </FormContainer>
  );
}
